import { useState, useEffect } from 'react'
import { Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import PropTypes from 'prop-types'

const { Dragger } = Upload

const StyledDragger = styled(Dragger)`
  .ant-upload-drag {
    height: 200px !important;
  }
  .ant-upload-list {
    max-height: 150px;
    overflow-y: auto;
  }
`

function ServiceProviderUpload({
  config,
  onUpload,
  onRemove,
  initialFiles = [],
}) {
  const [fileList, setFileList] = useState(initialFiles)
  const { t } = useTranslation()

  const acceptString = config.accepted_extensions
    .map((ext) => `.${ext}`)
    .join(',')

  useEffect(() => {
    if (fileList.length === 0 && initialFiles.length > 0) {
      setFileList(initialFiles)
    }
  }, [initialFiles])

  return (
    <StyledDragger
      accept={acceptString}
      multiple={config.max_files !== 1}
      maxCount={config.max_files ?? undefined}
      fileList={fileList}
      onChange={({ fileList }) => setFileList(fileList)}
      customRequest={onUpload}
      onRemove={(file) => {
        if (file.status === 'error') {
          return true
        }
        return onRemove(file)
      }}
      beforeUpload={(file) => {
        if (config.max_files && fileList.length >= config.max_files) {
          message.error(
            t('bookedservices.upload.max_files_reached', {
              count: config.max_files,
            }),
          )
          return Upload.LIST_IGNORE
        }
        const ext = file.name.split('.').pop()?.toLowerCase()
        if (!config.accepted_extensions.includes(ext)) {
          message.error(t('bookedservices.upload.invalid_extension', { ext }))
          return Upload.LIST_IGNORE
        }
        if (
          config.max_file_size_mb &&
          file.size > config.max_file_size_mb * 1024 * 1024
        ) {
          message.error(
            t('bookedservices.upload.file_too_large', {
              size: config.max_file_size_mb,
            }),
          )
          return Upload.LIST_IGNORE
        }
        return true
      }}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p>{t('bookedservices.upload.drag_or_click')}</p>
      <p className="ant-upload-hint">
        {t('bookedservices.upload.allowed_formats', {
          formats: config.accepted_extensions
            .map((e) => e.toUpperCase())
            .join(', '),
        })}
        {config.max_files &&
          ` · ${t('bookedservices.upload.max_files', { count: config.max_files })}`}
        {config.max_file_size_mb &&
          ` · ${t('bookedservices.upload.max_file_size', { size: config.max_file_size_mb })}`}
      </p>
    </StyledDragger>
  )
}

ServiceProviderUpload.propTypes = {
  config: PropTypes.shape({
    max_files: PropTypes.number,
    max_file_size_mb: PropTypes.number,
    accepted_extensions: PropTypes.array,
  }).isRequired,
  onUpload: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  initialFiles: PropTypes.array,
}

export default ServiceProviderUpload
